import React from 'react';
import { Button } from '@mantine/core';
import { useWorkflowChrome, type WorkflowToolbarAction } from '../WorkflowChromeContext';
import { LeftSlot, RightSlot, ToggleWrap, ToolbarBar } from './style';

const variantToProps = (variant: WorkflowToolbarAction['variant'] = 'primary') => {
  if (variant === 'ghost') return { variant: 'subtle' as const };
  if (variant === 'secondary') return { variant: 'outline' as const };
  return { variant: 'filled' as const };
};

const renderAction = (action: WorkflowToolbarAction | undefined, key: string) => {
  if (!action) return null;
  return (
    <Button
      key={key}
      size={action.size ?? 'sm'}
      onClick={action.onClick}
      disabled={action.disabled}
      loading={action.loading}
      {...variantToProps(action.variant)}
    >
      {action.label}
    </Button>
  );
};

const WorkflowToolbar: React.FC = () => {
  const {
    toolbarSlots,
    toolbarLeftContent,
    toolbarRightContent,
    showMultiAifToggle,
    multiAifView,
    setMultiAifView
  } = useWorkflowChrome();

  const saveAction = toolbarSlots?.saveAction;
  const exportAction = toolbarSlots?.exportAction;
  const primaryAction = toolbarSlots?.primaryAction;

  const hasAnyRight = !!(
    saveAction ||
    exportAction ||
    primaryAction ||
    showMultiAifToggle ||
    toolbarRightContent
  );
  const hasAnyLeft = !!toolbarLeftContent;

  // Hide entirely when nothing is registered to keep pages with no chrome
  // toolbar from showing an empty bar.
  if (!hasAnyLeft && !hasAnyRight) return null;

  return (
    <ToolbarBar>
      <LeftSlot>{toolbarLeftContent}</LeftSlot>
      <RightSlot>
        {toolbarRightContent}
        {showMultiAifToggle && (
          <ToggleWrap>
            <input
              type="checkbox"
              checked={multiAifView}
              onChange={e => setMultiAifView(e.target.checked)}
              aria-label="Multi-AIF View"
            />
            Multi-AIF View
          </ToggleWrap>
        )}
        {renderAction(saveAction, 'save')}
        {renderAction(exportAction, 'export')}
        {renderAction(primaryAction, 'primary')}
      </RightSlot>
    </ToolbarBar>
  );
};

export default WorkflowToolbar;
